import { Header } from './Header';
import { ExternalLink, AlertTriangle, Database, Calendar } from 'lucide-react';

interface Tool {
  id: string;
  name: string;
  tagline: string;
  description: string;
  url: string;
  icon: any;
  features: string[];
  status: 'live' | 'beta';
}

const tools: Tool[] = [
  {
    id: 'ewn-agent',
    name: 'EWN AI Agent',
    tagline: 'Intelligent early warning system',
    description: 'Detects programme delays, parses contract clauses, and drafts ready-to-raise Early Warning Notices.',
    url: 'https://movar-ewn-agent.netlify.app/',
    icon: AlertTriangle,
    features: ['Automated delay detection', 'Contract clause analysis', 'Ready-to-raise EWN generation', 'AI risk assessment'],
    status: 'live',
  },
  {
    id: 'data-mapper',
    name: 'Data Mapper',
    tagline: 'Semantic data mapping',
    description: 'Map data between unrelated tables using semantic matching and entity categorisation. Perfect for assigning cost codes to activities.',
    url: 'https://movar-data-mapper.netlify.app/',
    icon: Database,
    features: ['Semantic matching engine', 'Entity categorisation', 'Cost code assignment', 'Schedule data mapping'],
    status: 'live',
  },
  {
    id: 'schedule-analyser',
    name: 'Schedule Analyser',
    tagline: 'Comprehensive schedule intelligence',
    description: 'Upload XER files for a 4-week look-ahead and a detailed breakdown of every schedule table.',
    url: 'https://movar-schedule-analyser.netlify.app',
    icon: Calendar,
    features: ['XER file parsing', '4-week lookahead', 'Table breakdowns', 'Schedule analytics'],
    status: 'live',
  },
];

export function MovarToolsPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* ─── HERO ──────────────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-16 lg:pt-24 pb-16 lg:pb-20 w-full">
        <div className="grid grid-cols-12 gap-x-6">
          <div className="col-span-12 lg:col-span-10">
            <p className="eyebrow text-primary mb-6">Movar Dev Lab</p>
            <h1 className="display-lg text-white mb-6">
              Built things.<br />
              Free to play with.
            </h1>
            <p className="text-lg md:text-xl text-white/65 max-w-2xl leading-snug">
              A small set of experiments and demos from Movar Reply engineers. Each one is a thin
              slice of a real project-controls idea, put online so you can try it without an NDA.
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 w-full">
        <div className="hairline" />
      </div>

      {/* ─── TOOL LIST ─────────────────────────────────────────────── */}
      <main className="flex-1 max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-20 w-full">
        <ul className="grid grid-cols-12 gap-x-6 gap-y-10 md:gap-y-14">
          {tools.map((tool, index) => {
            const Icon = tool.icon;
            return (
              <li
                key={tool.id}
                className="col-span-12 md:col-span-6 lg:col-span-4 group"
                style={{ animation: `fade-up 0.6s ${index * 60}ms ease-out both` }}
              >
                <a href={tool.url} target="_blank" rel="noopener noreferrer" className="block">
                  <div className="border-t border-white/10 group-hover:border-primary transition-colors pt-6">
                    <div className="flex items-start justify-between mb-5">
                      <p className="eyebrow text-white/40">
                        {String(index + 1).padStart(2, '0')}
                      </p>
                      <ExternalLink className="w-4 h-4 text-white/30 group-hover:text-primary transition-colors" />
                    </div>

                    <div className="flex items-center gap-3 mb-3">
                      <Icon className="w-5 h-5 text-primary shrink-0" strokeWidth={1.75} />
                      <h3 className="text-2xl font-bold text-white group-hover:text-primary transition-colors leading-tight">
                        {tool.name}
                      </h3>
                    </div>

                    <p className="text-sm text-white/50 mb-4 italic">{tool.tagline}</p>

                    <p className="text-white/70 leading-relaxed mb-6">{tool.description}</p>

                    <ul className="space-y-1.5">
                      {tool.features.map((feature) => (
                        <li key={feature} className="flex items-baseline gap-2 text-sm text-white/55">
                          <span className="text-primary leading-none">·</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </a>
              </li>
            );
          })}
        </ul>
      </main>

      <footer className="border-t border-white/10 mt-auto">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-8">
          <p className="eyebrow text-white/40">&copy; 2026 Movar Reply</p>
          <p className="text-sm text-white/45 italic">big enough to deliver, small enough to care</p>
          <p className="eyebrow text-white/30 md:ml-auto">A Reply Group company</p>
        </div>
      </footer>
    </div>
  );
}
